import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import IconPay from '@/assets/icons/heroIcons/Pay.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconShopping from '@/assets/icons/heroIcons/Shopping-cart.svg';
import IconClipboardList from '@/assets/icons/heroIcons/ClipboardList.svg';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import Hero from '@/sections/Hero';
import MoreInfoSection from '@/sections/MoreInfo';
import { Button, DownloadButton } from '@/components/buttons';
import { InfoBoxes } from '@/components/boxes';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { SectionHeadline } from '@/components/SectionHeadline';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import React, { useRef, useState } from 'react';
import { Products } from '@/sections/forEshops/Products';
import Eshops from '@/sections/Eshops';
import { Testimonials as TestimonialsSection } from '@/sections/Testimonials';
import { SafeBet } from '@/sections/forEshops/SafeBet';
import IconLoan from '@/assets/icons/heroIcons/Loan.svg';
import { DXHeroes } from '@/sections/forEshops/DXHeroes';
import { Integration } from '@/sections/forEshops/Integration';
import { ContactForm } from '@/sections/forEshops/ContactForm';
import { motion } from 'framer-motion';
import { FAQs } from '@/components/seo/FAQs';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { getTestimonialsBySlug, Testimonial } from '@/api/sanity/testimonials';
import { SeoHeader } from '@/components/SeoHeader';
import { Text } from '@/components/typography';
import { type EshopsList, getAffiliateEshopsList } from '@/api/sanity/affiliateEshopsList';
import { useIsSelectedLanguage } from '@/hooks/useIsSelectedLanguage';
import IconShoppingCart from '@/assets/icons/heroIcons/Shopping-cart.svg';
import IconMobile from '@/assets/icons/heroIcons/Device-mobile.svg';
import IconUser from '@/assets/icons/heroIcons/User-group.svg';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { getSectionAnchorId } from '@/utils/anchors';
interface Props extends IntlProps {
  menuPromoBanners?: PageMenuPromoBanner[];
  pageBanner: PageBanner | null;
  eshops: EshopsList;
  testimonials: Testimonial[];
}
export default function Page(props: Props) {
  usePageView('forEshops');
  const {
    t
  } = useTranslation(['forEshops', 'core']);
  const {
    eshops,
    menuPromoBanners,
    pageBanner,
    testimonials
  } = props;
  const contactFormRef = useRef<HTMLDivElement>(null);
  const productsRef = useRef<HTMLDivElement>(null);
  const [selectedInterest, setSelectedInterest] = useState<string | undefined>(undefined);
  const isCsLanguage = useIsSelectedLanguage('cs');
  const handleScrollToContact = (interest?: string) => {
    if (contactFormRef.current) {
      if (interest) {
        setSelectedInterest(interest);
      }
      contactFormRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleScrollToProducts = (interest_0?: string) => {
    if (productsRef.current) {
      if (interest_0) {
        setSelectedInterest(interest_0);
      }
      productsRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <Layout menuPromoBanners={menuPromoBanners}>
			<SeoHeader title={t('seo.title')} description={t('seo.description')} />
			<div className="relative overflow-x-hidden">
				<Hero videoId={pageBanner?.videoId} title={pageBanner?.title ?? t('banner.title')} text={pageBanner?.text ?? t('banner.text')} buttons={pageBanner ? pageBanner.buttons?.length ? [{
        ...pageBanner.buttons[0],
        to: undefined,
        onClick: handleScrollToContact
      }, pageBanner.buttons[1]].filter(Boolean) : null : [{
        ariaLabel: t('banner.cta.1'),
        label: t('banner.cta.1'),
        onClick: handleScrollToContact,
        testId: 'forEshopsHeroPrimaryCTA',
        analyticsParam: 'forEshopsHeroPrimaryCTA',
        variant: 'primary'
      }, {
        ariaLabel: t('banner.cta.2'),
        label: t('banner.cta.2'),
        onClick: handleScrollToProducts,
        testId: 'forEshopsHeroSecondaryCTA',
        analyticsParam: 'forEshopsHeroSecondaryCTA',
        variant: 'secondary'
      }]} image={{
        url: pageBanner?.image?.url ?? '/images/sections/forEshops/hero/cs/hero.png',
        alt: pageBanner?.image?.alt ?? t('banner.title')
      }} variant={pageBanner?.variant || 'mockupMobile'} />

				<div className="bg-blue-50">
					<SectionDelimiter color="bg-blue-100" background="bg-transparent" />
				</div>

				<div className="relative z-30 bg-blue-100 px-4 py-10 lg:py-20">
					<SafeBet />
				</div>
				<div className="relative bg-blue-100 px-4 py-10 lg:py-20" id={getSectionAnchorId('why')}>
					<div className="relative z-10 mx-auto max-w-[48rem]">
						<SectionHeadline className="mb-10 px-0 lg:mb-16" anchorKey="why.title">
							{t('why.title')}
						</SectionHeadline>
						<InfoBoxes items={[{
            title: t('why.items.1.title'),
            paragraph: t('why.items.1.text'),
            icon: <IconPay />
          }, {
            title: t('why.items.2.title'),
            paragraph: t('why.items.2.text'),
            icon: <IconLoan />
          }, {
            title: t('why.items.3.title'),
            paragraph: t('why.items.3.text'),
            icon: <IconShopping />
          }, {
            title: t('why.items.4.title'),
            paragraph: t('why.items.4.text'),
            icon: <IconClipboardList />
          }]} variant="basic" />
					</div>
					<Button onClick={handleScrollToContact} testId="forEshopsIWantIt" analyticsParam="forEshopsIWantIt" size="lg" variant="primary" align="center" className="mt-12 lg:mt-16">
						{t('why.cta')}
					</Button>
				</div>
			</div>
			<div id={getSectionAnchorId('products')} className="4xl:px-16 relative scroll-mt-16 bg-blue-100 py-10 lg:py-20 2xl:px-7" ref={productsRef}>
				<Products onClick={handleScrollToContact} />
			</div>
			<div className="relative bg-blue-100 px-4 py-10 pb-20 lg:py-20 lg:pb-40">
				<Integration showBubble>
					<DXHeroes />
				</Integration>
			</div>

			<SectionDelimiter color="bg-blue-50" background="bg-blue-100" />

			<div className="relative overflow-hidden bg-blue-50">
				<div className="bgCircle absolute bottom-[30rem] left-0 -translate-x-1/4 rotate-180 lg:top-1/2" />
				<div className="bgCircle absolute right-0 bottom-24 translate-x-1/4 lg:hidden" />
				<div className="relative z-10">
					{eshops.length > 0 && <div className="py-10 lg:py-20">
							<Eshops title={t('eShops.title')} text={t('eShops.text')} button={<Button onClick={handleScrollToContact} testId="forEshopsIWantIt" analyticsParam="forEshopsIWantIt" size="lg" variant="primary" align="center" className="mt-12">
										{t('why.cta')}
									</Button>} eshops={eshops} />
						</div>}
					{isCsLanguage && <div className="px-4 py-10 lg:py-20" id={getSectionAnchorId('marketing')}>
							<SectionHeadline className="mb-4 lg:mb-6" anchorKey="marketing.title">
								{t('marketing.title')}
							</SectionHeadline>
							<Text variant="p2" className="text-center text-gray-800">
								{t('marketing.text')}
							</Text>
							<div className="mt-10 text-center lg:mt-16">
								<div className="mx-auto inline-block">
									{/*
          <div className="mb-2">
          <DownloadButton
          to="/files/cs/Skippay_Cenik.pdf"
          testId="forEshopsMarketingPricing"
          analyticsParam="forEshopsMarketingPricing"
          >
          {t('marketing.files.1')}
          </DownloadButton>
          </div>
          */}
									<div className="mb-2">
										<DownloadButton to="/files/cs/Skippay_Moznosti_propagace_pro-eshopy.pdf" testId="forEshopsMarketingPackages" analyticsParam="forEshopsMarketingPackages">
											{t('marketing.files.2')}
										</DownloadButton>
									</div>
									<div className="mb-2">
										<DownloadButton to="https://kulyma.sharepoint.com/:f:/s/Public/ErqHaMMhfkJCmgIsSWBmnx8B8gp_Ku4Gm75ZZoeGQIYZZA?e=zg6DB0" testId="forEshopsMarketingMaterials" analyticsParam="forEshopsMarketingMaterials">
											{t('marketing.files.3')}
										</DownloadButton>
									</div>
								</div>
							</div>
						</div>}
					<div className="pt-10 pb-20 lg:pt-20 lg:pb-40">
						<TestimonialsSection title={t('testimonials.title')} testimonials={testimonials} />
					</div>
				</div>
			</div>

			<SectionDelimiter color="bg-blue-700" background="bg-blue-50" />

			<div className="relative overflow-hidden bg-blue-700 px-4">
				<div className="mx-auto max-w-192 py-10 lg:py-20">
					<SectionHeadline className="mb-10 px-0 lg:mb-16" variant="inverted" anchorKey="faq.title" id="faq">
						{t('faq.title')}
					</SectionHeadline>

					<FAQs hideButton className="m-0!" bgColor="bg-blue-100" data={[{
          question: t('faq.items.1.title'),
          answer: <Trans t={t} i18nKey="faq.items.1.text">
										<Button variant="unstyled" className="text-blue-500 underline decoration-1 hover:text-blue-800" testId="contactForm" analyticsParam="contactForm" onClick={handleScrollToContact} />
									</Trans>
        }, {
          question: t('faq.items.2.title'),
          answer: t('faq.items.2.text')
        }, {
          question: t('faq.items.3.title'),
          answer: t('faq.items.3.text')
        }]} />
				</div>
				<motion.div className="mx-auto max-w-192 scroll-mt-16 pt-10 pb-20 lg:pt-20 lg:pb-40" ref={contactFormRef}>
					<SectionHeadline className="mb-10 px-0 lg:mb-16" variant="inverted" anchorKey="forEshops.form.title">
						{t('form.title')}
					</SectionHeadline>

					<ContactForm initialInterest={selectedInterest} variant="forEshops" />
				</motion.div>
			</div>
			<SectionDelimiter color="bg-blue-100" background="bg-blue-700" />

			<div className="relative overflow-hidden bg-blue-100">
				<div className="relative z-10 px-4">
					<MoreInfoSection buttons={isCsLanguage ? [{
          analyticsParam: 'forEshopsMoreInfoDeferredPayment',
          icon: <IconDeferredPayment className="fillButtonIcon" />,
          testId: 'forEshopsMoreInfoDeferredPayment',
          title: t('core:link.deferredPayment'),
          to: LINKS().deferredPayment
        }, {
          analyticsParam: 'forEshopsMoreInfoRevolvingApplication',
          icon: <IconMaxi className="fillButtonIcon" />,
          testId: 'forEshopsMoreInfoRevolvingApplication',
          title: t('core:products.revolving'),
          to: LINKS().revolving
        }, {
          analyticsParam: 'forEshopsMoreInfoPayInThree',
          icon: <IconThird className="fillButtonIcon" />,
          testId: 'forEshopsMoreInfoPayInThree',
          title: t('core:products.payInThree'),
          to: LINKS().payInThree
        }, {
          analyticsParam: 'forEshopsMoreInfoCard',
          icon: <IconCard className="fillButtonIcon" />,
          testId: 'forEshopsMoreInfoCard',
          title: t('core:products.card'),
          to: LINKS().card
        }] : [{
          analyticsParam: 'forEshopsMoreInfoPayInThree',
          icon: <IconThird className="fillButtonIcon" />,
          testId: 'forEshopsMoreInfoPayInThree',
          title: t('core:products.payInThree'),
          to: LINKS().payInThree
        }, {
          analyticsParam: 'forEshopsMoreInfoWhereToShop',
          icon: <IconShoppingCart className="fillButtonIcon" />,
          testId: 'forEshopsMoreInfoWhereToShop',
          title: t('core:products.whereToShop'),
          to: LINKS().whereToShop
        }, {
          analyticsParam: 'forEshopsMobileApp',
          icon: <IconMobile className="fillButtonIcon" />,
          testId: 'forEshopsMoreInfoMobileApp',
          title: t('core:products.mobileApp'),
          to: LINKS().mobileApp
        }, {
          analyticsParam: 'forEshopsMoreInfoAboutCompany',
          icon: <IconUser className="fillButtonIcon" />,
          testId: 'forEshopsMoreInfoAboutCompany',
          title: t('core:link.aboutUs'),
          to: LINKS().aboutCompany
        }]} />
				</div>
				<div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />
			</div>
		</Layout>;
}
export const getStaticProps = (async ctx => {
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      pageBanner: await getPageBannerBySlug('for-eshops'),
      testimonials: await getTestimonialsBySlug('for-eshops'),
      eshops: await getAffiliateEshopsList('default'),
      ...(await getIntlProps(ctx, ['core', 'forEshops', 'testimonials']))
    }
  };
}) satisfies GetStaticProps<Props>;